import type { RouteObject } from 'react-router';
import { Navigate } from 'react-router';
import { AppShell } from './shell/AppShell';
import { DesignSheet } from './design/DesignSheet';
import { Start } from './pages/Start';
import { StopMail } from './pages/StopMail';
import { Today } from './pages/Today';
import { Open } from './pages/Open';
import { Meetings } from './pages/Meetings';
import { Calendar } from './pages/Calendar';
import { Meeting } from './pages/Meeting';
import { People } from './pages/People';
import { PersonPage } from './pages/Person';
import { Series } from './pages/Series';
import { SeriesIndex } from './pages/SeriesIndex';
import { Knowledge } from './pages/Knowledge';
import { AnswerPage } from './pages/Answer';
import { CommitmentPage } from './pages/Commitment';
import { DiscussionPage } from './pages/Discussion';
import { Topics } from './pages/Topics';
import { Topic } from './pages/Topic';
import { Channels } from './pages/Channels';
import { ChannelPage } from './pages/Channel';
import { Search } from './pages/Search';
import { Settings } from './pages/Settings';
import { Organisation, RecordingDefaults } from './pages/Organisation';
import { Analytics } from './pages/Analytics';
import { AuditLog } from './pages/AuditLog';
import { Connected } from './pages/Connected';
import { ImportPage } from './pages/Import';
import { NotFound } from './pages/NotFound';
import { ReleaseNotes } from './pages/ReleaseNotes';
import { Templates } from './pages/Templates';
import { Notifications } from './settings/Notifications';

// The places, in the plain vocabulary (docs/redesign-v2 + the artboard
// inventory in design/INVENTORY.md):
//   /            Today — three equal thirds
//   /open        what is unfinished — two columns by direction
//   /meetings    when it happened, and the week ahead (?space= ?folder= browse by container)
//   /m/:id       one meeting — four views behind one switch
//   /people      who it is with — people, series, companies; /people/:email, /series/:key
//   /knowledge   what stuck (day-one state until Phase 5)
//   /channels    groups of people, and the meetings posted to them (PRD 58)
//   /search      matches names and sentences
//   /release-notes  what changed, minors and majors only (2026-09-25)
//   /templates   the shapes a meeting can be written up in, beside its note (PRD 61)
//   /settings    yours; /settings/connected — apps and tokens; /settings/import — bring a
//                history in from Fellow; /settings/organisation — people
//                and roles; /settings/organisation/defaults — the workspace's recording defaults
//   /start       sign in · connect the calendar
//   /design      the design system, live
export const routes: RouteObject[] = [
  {
    element: <AppShell />,
    children: [
      { path: '/', element: <Today /> },
      { path: '/open', element: <Open /> },
      { path: '/meetings', element: <Meetings /> },
      // One week at a time, a column a day (PRD 67).
      { path: '/calendar', element: <Calendar /> },
      { path: '/m/:id', element: <Meeting /> },
      { path: '/people', element: <People /> },
      { path: '/people/:email', element: <PersonPage /> },
      { path: '/series', element: <SeriesIndex /> },
      { path: '/series/:key', element: <Series /> },
      { path: '/knowledge', element: <Knowledge /> },
      { path: '/ask', element: <AnswerPage /> },
      { path: '/answers/:answerId', element: <AnswerPage /> },
      { path: '/m/:id/answer', element: <AnswerPage /> },
      { path: '/c/:id', element: <CommitmentPage /> },
      // ⚠ `/d/` — the episodic cluster is a Discussion now (PRD 52), and `/t/`
      // belongs to the subject that keeps coming back.
      { path: '/d/:id', element: <DiscussionPage /> },
      // The subjects one person keeps coming back to (PRD 52).
      { path: '/topics', element: <Topics /> },
      { path: '/t/:id', element: <Topic /> },
      // Channels replace spaces and folders (PRD 58); the old addresses land
      // on Channels rather than on a page that no longer exists.
      { path: '/channels', element: <Channels /> },
      { path: '/ch/:id', element: <ChannelPage /> },
      { path: '/f/:id', element: <Navigate to="/channels" replace /> },
      { path: '/spaces', element: <Navigate to="/channels" replace /> },
      { path: '/search', element: <Search /> },
      { path: '/release-notes', element: <ReleaseNotes /> },
      { path: '/templates', element: <Templates /> },
      { path: '/settings', element: <Settings /> },
      { path: '/settings/connected', element: <Connected /> },
      { path: '/settings/notifications', element: <Notifications /> },
      { path: '/settings/import', element: <ImportPage /> },
      { path: '/settings/organisation', element: <Organisation /> },
      { path: '/settings/organisation/defaults', element: <RecordingDefaults /> },
      { path: '/analytics', element: <Analytics /> },
      { path: '/settings/organisation/analytics', element: <Navigate to="/analytics" replace /> },
      { path: '/settings/organisation/log', element: <AuditLog /> },
      { path: '/start', element: <Start /> },
      { path: '/stop', element: <StopMail /> },
      { path: '/design', element: <DesignSheet /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];
